import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Alert from 'utils/packages/Alert';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { AppView, Button, ButtonGroup, CardView, rem, SCROLL_INDICATOR_INSETS, SettingsItem, StatusBar, withAnimatedNavigationHeader, } from 'design-system';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import FlatListItemCard from 'design-system/FlatListItemCard';
import { SelectableAccountCard } from 'design-system/SelectableAccountCard';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import getListOfflineAccounts from 'utils/api/getListOfflineAccounts';
import { selectActiveConnections } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { sortAccountsByType } from 'utils/api/sortAccounts';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { useMarginBottom } from 'hooks/useMarginBottom';
import IconPlus from 'design-system/icons/IconPlus';
import useColors from 'hooks/useColors';
import Text from 'design-system/Text';
import Squircle from 'design-system/Squircle';
import { IconBank } from 'design-system/icons';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { PHYSICAL_ASSET_TYPES } from 'features/accounts/constants';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
const strings = {
    title: 'Pick an offline account',
    subtitle: 'Whenever we detect a transaction on your selected accounts, an equivalent entry will be recorded on this offline account',
    emptyTitle: 'You haven’t made any offline accounts',
    emptySubTitle: 'To create a smart rule, you need to have an offline account to transfer transactions detected by the rule',
};
const keyExtractor = (item) => item.id.toString();
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const SmartRulePickOfflineAccountScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const styles = useStyles(styleSet);
    const [selected, setSelected] = useState();
    const activeConnections = useAppSelector(selectActiveConnections);
    const accounts = useMemo(() => {
        const accounts = getListOfflineAccounts(activeConnections, false).filter((account) => !PHYSICAL_ASSET_TYPES.has(account.type));
        const copy = sortAccountsByType(accounts);
        copy.sort((a, b) => Number(a.isHidden) - Number(b.isHidden));
        return copy;
    }, [activeConnections]);
    const colors = useColors();
    const onAddAccount = useCallback(() => {
        navigation.navigateDeprecated('PickAccountType', {
            keyBack: route.key,
        });
    }, [route.key]);
    const renderItem = useCallback(({ item, index }) => (<>
            {index === 0 && (<FlatListItemCard index={index} style={styles.card} onPress={onAddAccount} itemsLength={accounts.length + 1}>
                <SettingsItem title="Create new offline account" renderIcon={() => (<View style={styles.addNewAccount}>
                      <IconPlus color={colors.text.brand}/>
                    </View>)}/>
              </FlatListItemCard>)}
            <FlatListItemCard index={index + 1} style={styles.card} itemsLength={accounts.length + 1} onPress={() => setSelected(item.id)}>
              <View pointerEvents="none">
                <SelectableAccountCard data={item} transparent type="single" showHiddenLogo isSelected={selected === item.id}/>
              </View>
            </FlatListItemCard>
          </>), [
        accounts.length,
        onAddAccount,
        colors.text.brand,
        selected,
        styles.addNewAccount,
        styles.card,
    ]);
    const ListHeaderComponent = useMemo(() => (<ScreenInfoHeader title={strings.title} subTitle={strings.subtitle}/>), []);
    const ListEmptyComponent = useMemo(() => (<CardView noMarginTop style={styles.emptyContainer}>
            <Squircle width={rem(72)} height={rem(72)} style={styles.icon}>
              <IconBank colorKey="secondaryLight" width={rem(40)} height={rem(40)}/>
            </Squircle>
            <Text Text-16 centered>
              {strings.emptyTitle}
            </Text>
            <Text TextThin-14 Secondary centered style={styles.subTitle}>
              {strings.emptySubTitle}
            </Text>
            <Button brandLight small hug title="Create an offline account" onPress={onAddAccount}/>
          </CardView>), [onAddAccount, styles.emptyContainer, styles.icon, styles.subTitle]);
    const handleContinue = useCallback(() => {
        if (!selected) {
            Alert.alert('Hold on 💫', 'You need to select an account.');
            return;
        }
        navigation.navigateDeprecated('SmartRuleReview', {
            ...route.params,
            targetAccountId: selected,
        });
    }, [route.params, selected]);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    return (<AppView withFrame={false}>
          <StatusBar barStyle="dark-content" animated/>
          <AnimatedFlashList data={accounts} renderItem={renderItem} keyExtractor={keyExtractor} onScroll={scrollHandler} scrollEventThrottle={16} extraData={[selected, colors.cards.onDark]} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={paddingBottom} ListHeaderComponent={ListHeaderComponent} ListEmptyComponent={ListEmptyComponent}/>
          <ButtonGroup type="floating">
            <Button brand title="Continue" disabled={!accounts.length} onPress={handleContinue}/>
          </ButtonGroup>
        </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    card: {
        backgroundColor: colors.cards.onDark,
    },
    addNewAccount: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(50),
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: colors.background.dark,
        marginRight: rem(16),
    },
    emptyContainer: {
        padding: rem(24),
    },
    icon: {
        alignSelf: 'center',
        marginBottom: rem(16),
    },
    subTitle: {
        marginTop: rem(4),
        marginBottom: rem(16),
    },
}));
export default SmartRulePickOfflineAccountScreen;
